import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Helper function to check if a date is today
const isToday = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

// Helper function to check if a booking is currently "in hotel"
const isInHotel = (checkInDateString, checkOutDateString, status) => {
  if (status === 'Cancelled') return false;
  const checkIn = new Date(checkInDateString);
  const checkOut = new Date(checkOutDateString);
  const today = new Date();
  checkIn.setHours(0, 0, 0, 0);
  checkOut.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return today >= checkIn && today <= checkOut;
};

// Updated Modal Component with Specific Image Labels and Previews
const SingleRoomEditModal = ({ isOpen, onClose, room, onSave }) => {
  const [formData, setFormData] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});
  const [previews, setPreviews] = useState({});

  useEffect(() => {
    if (room) {
      setFormData({
        id: room.id,
        room_type: room.type || '',
        editableInventoryCapacity: room.editableInventoryCapacity || 0,
        price: room.price || 0,
        deals: room.deals || '',
        dealPrice: room.dealPrice || '',
      });
      setSelectedFiles({});
      // Set initial previews from existing database images (Base64)
      setPreviews({
        i1: room.i1 ? `data:image/jpeg;base64,${room.i1}` : null,
        i2: room.i2 ? `data:image/jpeg;base64,${room.i2}` : null,
        i3: room.i3 ? `data:image/jpeg;base64,${room.i3}` : null,
        i4: room.i4 ? `data:image/jpeg;base64,${room.i4}` : null,
        i5: room.i5 ? `data:image/jpeg;base64,${room.i5}` : null,
        i6: room.i6 ? `data:image/jpeg;base64,${room.i6}` : null,
      });
    }
  }, [room]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, key) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFiles(prev => ({ ...prev, [key]: file }));
      // Create local preview URL
      setPreviews(prev => ({ ...prev, [key]: URL.createObjectURL(file) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append('room_type', formData.room_type);
    data.append('available', formData.editableInventoryCapacity);
    data.append('price', formData.price);
    data.append('deals', formData.deals);
    data.append('dealPrice', formData.dealPrice);

    for (let i = 1; i <= 6; i++) {
      if (selectedFiles[`i${i}`]) {
        data.append(`i${i}`, selectedFiles[`i${i}`]);
      }
    }

    if (onSave) {
      await onSave(data, formData.id);
    }
    onClose();
  };

  const renderImageUpload = (key, label) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '10px' }}>
      <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#555' }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '50px', height: '40px', background: '#f0f0f0', borderRadius: '4px', overflow: 'hidden', border: '1px solid #ddd' }}>
          {previews[key] ? <img src={previews[key]} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ fontSize: '8px', textAlign: 'center', marginTop: '12px' }}>No Img</div>}
        </div>
        <input type="file" onChange={(e) => handleFileChange(e, key)} style={{ fontSize: '11px', width: '100%' }} accept="image/*" />
      </div>
    </div>
  );

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.modalContent}>
        <h3 style={modalStyles.modalTitle}>Update {room?.type}</h3>
        <form onSubmit={handleSubmit} style={modalStyles.form}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <label style={modalStyles.label}> Room Type:
              <input type="text" name="room_type" value={formData.room_type || ''} onChange={handleChange} style={modalStyles.input} required />
            </label>
            <label style={modalStyles.label}> Total Rooms:
              <input type="number" name="editableInventoryCapacity" value={formData.editableInventoryCapacity || ''} onChange={handleChange} style={modalStyles.input} required />
            </label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <label style={modalStyles.label}> Price (Rs.):
              <input type="number" name="price" value={formData.price || ''} onChange={handleChange} style={modalStyles.input} required />
            </label>
            <label style={modalStyles.label}> Deal Price:
              <input type="number" name="dealPrice" value={formData.dealPrice || ''} onChange={handleChange} style={modalStyles.input} />
            </label>
          </div>

          <label style={modalStyles.label}> Deal Tag:
            <input type="text" name="deals" value={formData.deals || ''} onChange={handleChange} style={modalStyles.input} />
          </label>

          <hr style={{ border: '0.5px solid #eee', margin: '10px 0' }} />
          
          {/* Image Section - 2 Columns to avoid scroll */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {renderImageUpload('i1', 'Home Image')}
            {renderImageUpload('i2', 'Room Image')}
            {renderImageUpload('i3', 'Room Sub-Img 1')}
            {renderImageUpload('i4', 'Room Sub-Img 2')}
            {renderImageUpload('i5', 'Room Sub-Img 3')}
            {renderImageUpload('i6', 'Room Sub-Img 4')}
          </div>

          <div style={modalStyles.buttonContainer}>
            <button type="submit" style={modalStyles.saveButton}>Save Changes</button>
            <button type="button" onClick={onClose} style={modalStyles.cancelButton}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const modalStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modalContent: { backgroundColor: '#fff', padding: '25px', borderRadius: '12px', width: '95%', maxWidth: '600px', boxSizing: 'border-box', maxHeight: '98vh' },
  modalTitle: { fontSize: '20px', fontWeight: '700', marginBottom: '15px', textAlign: 'center' },
  form: { display: 'flex', flexDirection: 'column', gap: '10px' },
  label: { fontSize: '13px', fontWeight: '600', display: 'flex', flexDirection: 'column', gap: '4px' },
  input: { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' },
  buttonContainer: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' },
  saveButton: { backgroundColor: '#ae7d54', color: 'white', padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600' },
  cancelButton: { backgroundColor: '#e5e7eb', color: '#374151', padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer' },
};

const RoomContainer = () => {
  const [fetchedRooms, setFetchedRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSingleEditModalOpen, setIsSingleEditModalOpen] = useState(false);
  const [selectedRoomToEdit, setSelectedRoomToEdit] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editableRooms, setEditableRooms] = useState([]);

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const inventoryRes = await axios.get('https://admin.thepridehotel.in/api/inventory');
      const roomsRes = await axios.get('https://admin.thepridehotel.in/api/rooms');
      
      const counts = {};
      roomsRes.data.forEach(r => {
        if (!counts[r.room_type]) counts[r.room_type] = { total: 0, available: 0 };
        counts[r.room_type].total++;
        if (r.status === 'Available') counts[r.room_type].available++;
      });

      const transformed = inventoryRes.data.map(item => ({
        id: item.id,
        type: item.room_type || '',
        actualAvailable: counts[item.room_type]?.available || 0,
        totalCapacity: counts[item.room_type]?.total || 0,
        editableInventoryCapacity: Number(item.available) || 0,
        price: Number(item.price) || 0,
        deals: item.deals || '',
        dealPrice: Number(item.dealPrice) || '',
        i1: item.i1, i2: item.i2, i3: item.i3, i4: item.i4, i5: item.i5, i6: item.i6
      }));

      setFetchedRooms(transformed);
      setEditableRooms(transformed.map(r => ({ ...r })));
    } catch (err) {
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSingleRoomChanges = async (formData, roomId) => {
    try {
      await axios.put(`https://admin.thepridehotel.in/api/inventory/${roomId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      fetchRooms();
      alert('Update Successful!');
    } catch (err) {
      alert('Update Failed');
    }
  };

  const handleInlineChange = (e, roomId) => {
    const { name, value } = e.target;
    setEditableRooms(prev => prev.map(r => r.id === roomId ? { ...r, [name]: value === '' ? '' : (name === 'deals' ? value : Number(value)) } : r));
  };

  const handleSaveAllInlineChanges = async () => {
    setLoading(true);
    try {
      const updates = editableRooms.map(room => {
        const original = fetchedRooms.find(f => f.id === room.id);
        if (JSON.stringify(original) !== JSON.stringify(room)) {
          return axios.put(`https://admin.thepridehotel.in/api/inventory/${room.id}`, {
            room_type: room.type,
            available: room.editableInventoryCapacity,
            price: room.price,
            deals: room.deals,
            dealPrice: room.dealPrice
          });
        }
        return null;
      }).filter(Boolean);
      if (updates.length > 0) await Promise.all(updates);
      setEditMode(false);
      fetchRooms();
    } catch (err) {
      alert('Bulk Update Failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRooms(); }, []);

  if (loading) return <div className="main-container">Loading...</div>;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap');
        .main-container { font-family: 'Open Sans', sans-serif; padding: 20px; background: #fff; border-radius: 10px; margin: 10px; box-shadow: 0 1px 4px rgba(0,0,0,0.05); }
        .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
        .update-btn { background: #ae7d54; color: white; padding: 8px 16px; border-radius: 6px; border: none; cursor: pointer; font-weight: 600; }
        .grid-container { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 15px; }
        .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px; position: relative; background: #fff; }
        .menu { position: absolute; top: 12px; right: 12px; width: 5px; height: 18px; background: repeating-linear-gradient(to bottom, #6b7280 0 2px, transparent 2px 4px); cursor: pointer; }
        .badge { background: #d1fae5; color: #047857; font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 5px; margin-bottom: 8px; display: inline-block; }
        .room-type-display { font-weight: 600; color: #374151; margin-bottom: 5px; }
        .count-display { font-size: 20px; font-weight: 700; }
        .price-display { color: #ae7d54; font-weight: 700; font-size: 18px; margin-top: 5px; }
        .total { color: #9ca3af; font-size: 14px; }
        .inline-input { width: 100%; margin-bottom: 5px; padding: 6px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px; }
      `}</style>

      <div className="main-container">
        <div className="header">
          <h2 style={{ fontSize: '18px', fontWeight: '700' }}>Rooms Overview</h2>
          <button className="update-btn" onClick={editMode ? handleSaveAllInlineChanges : () => setEditMode(true)}>
            {editMode ? 'Save All' : 'Edit Inventory'}
          </button>
        </div>

        <div className="grid-container">
          {(editMode ? editableRooms : fetchedRooms).map((room) => (
            <div key={room.id} className="card">
              {!editMode ? (
                <>
                  {room.deals && <div className="badge">{room.deals}</div>}
                  <div className="menu" onClick={() => { setSelectedRoomToEdit(room); setIsSingleEditModalOpen(true); }}></div>
                  <div className="room-type-display">{room.type}</div>
                  <div className="count-display">
                    {room.actualAvailable.toString().padStart(2, '0')}
                    <span className="total">/{room.totalCapacity.toString().padStart(2, '0')}</span>
                  </div>
                  <div className="price-display">Rs. {room.price.toLocaleString()}</div>
                </>
              ) : (
                <>
                  <input className="inline-input" name="type" value={room.type} onChange={(e) => handleInlineChange(e, room.id)} />
                  <input className="inline-input" type="number" name="editableInventoryCapacity" value={room.editableInventoryCapacity} onChange={(e) => handleInlineChange(e, room.id)} />
                  <input className="inline-input" type="number" name="price" value={room.price} onChange={(e) => handleInlineChange(e, room.id)} />
                  <input className="inline-input" name="deals" value={room.deals} onChange={(e) => handleInlineChange(e, room.id)} />
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <SingleRoomEditModal
        isOpen={isSingleEditModalOpen}
        onClose={() => setIsSingleEditModalOpen(false)}
        room={selectedRoomToEdit}
        onSave={handleSaveSingleRoomChanges}
      />
    </>
  );
};

export default RoomContainer;